import type { JSX } from 'react';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { useTenancyStore } from '../tenancy/tenancy-store';
import { formatPlanBillingLabel } from './planBillingLabels';
import { isPlanUsable, isTrial, isTrialExpired, type PlanStatusSource } from './planStatus';

function renderBlockedCopy(source: PlanStatusSource): string {
  if (!source) {
    return 'Select a company to continue.';
  }

  if (isTrial(source)) {
    return isTrialExpired(source)
      ? 'Your trial has expired and the plan is inactive until billing is set up.'
      : 'Trial details are missing; billing must be set up before you can use this feature.';
  }

  if (source.billingStatus === 'past_due') {
    return 'This subscription is past due. Update billing to regain access.';
  }

  if (source.billingStatus === 'canceled') {
    return 'This subscription is canceled. Reactivate billing to continue.';
  }

  return 'The plan for this company is inactive. Open billing to resolve it.';
}

export function RequirePlanUsable(): JSX.Element {
  const { currentUser, isLoading: isAuthLoading } = useAuth();
  const selectedCompanyId = useTenancyStore((state) => state.selectedCompanyId);
  const activeCompany = currentUser?.activeCompany?.company ?? null;

  if (isAuthLoading) {
    return (
      <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading your session...</Typography>
      </Stack>
    );
  }

  if (!selectedCompanyId || !activeCompany) {
    return (
      <Stack spacing={2.5}>
        <Typography variant="h4">Plan check</Typography>
        <Alert
          severity="info"
          action={
            <Button
              component={NavLink}
              to="/select-company"
              color="inherit"
              size="small"
              variant="outlined"
            >
              Select company
            </Button>
          }
        >
          We could not load a company context for this session. Select a company to continue.
        </Alert>
      </Stack>
    );
  }

  const planUsable = isPlanUsable(activeCompany);

  if (!planUsable) {
    return (
      <Stack spacing={2.5}>
        <Typography variant="h4">Plan required</Typography>
        <Alert
          severity="warning"
          action={
            <Button
              component={NavLink}
              to="/billing"
              color="inherit"
              size="small"
              variant="outlined"
            >
              Go to billing
            </Button>
          }
        >
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600}>
              {formatPlanBillingLabel(activeCompany)}
            </Typography>
            <Typography variant="body2">{renderBlockedCopy(activeCompany)}</Typography>
          </Stack>
        </Alert>
      </Stack>
    );
  }

  return <Outlet />;
}
